import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import { offerTemplates, type OfferTemplateInput } from '@/api/endpoints';
import type { OfferTemplate, WorkType } from '@/types';
import { Icon } from '@/components/Icon';
import { TiptapEditor } from '@/components/TiptapEditor';
import { Pagination, usePaginated } from '@/components/Pagination';
import { SearchableSelect } from '@/components/SearchableSelect';

const EMPTY: OfferTemplateInput = {
  code: '',
  name: '',
  workType: 'ANALIZA_RYZYKA',
  description: '',
  sampleSubject: '',
  sampleBody: '',
  sampleLegalBasis: '',
  sampleTerms: '',
  sampleExtraCosts: '',
  styleNotes: '',
  active: true,
};

export function OfferTemplateManagement() {
  const [rows, setRows] = useState<OfferTemplate[] | null>(null);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<OfferTemplate | 'new' | null>(null);

  const reload = () => {
    setRows(null); setErr(null);
    offerTemplates.adminList().then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(() => {
    reload();
    offerTemplates.workTypes().then(setWorkTypes).catch(() => {});
  }, []);

  const remove = (t: OfferTemplate) => {
    if (!confirm(`Usunąć wzorzec "${t.name}"?`)) return;
    offerTemplates.adminDelete(t.id).then(reload).catch(e => setErr(e?.message ?? String(e)));
  };

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'offer-templates' });

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Wzorce ofert (AI few-shots)</h1>
          <div className="text-ink-400 text-[13px]">Przykłady pism, których AI używa do generowania nowych ofert w Twoim stylu</div>
        </div>
        <button onClick={() => setEditing('new')} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5"/> Nowy wzorzec
        </button>
      </header>

      <div className="card">
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">Brak wzorców. Dodaj pierwszy.</div>
          )}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th className="w-44">Typ pracy</th>
                  <th>Opis</th>
                  <th className="w-24">Status</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(t => (
                  <tr key={t.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => setEditing(t)}>
                    <td>
                      <div className="font-semibold text-ink-900">{t.name}</div>
                      <div className="text-[11px] text-ink-400 font-mono">{t.code}</div>
                    </td>
                    <td>
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-water-100 text-water-700">
                        {t.workType}
                      </span>
                    </td>
                    <td className="text-[12px] text-ink-600 max-w-[400px] truncate">{t.description ?? '—'}</td>
                    <td>
                      <span className={'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ' +
                        (t.active ? 'bg-green-100 text-green-700' : 'bg-ink-100 text-ink-500')}>
                        {t.active ? 'Aktywny' : 'Wyłączony'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button onClick={() => remove(t)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500">
                        <Icon name="x"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      <TemplateEditor
        open={editing !== null}
        template={editing === 'new' ? null : editing}
        workTypes={workTypes}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

function TemplateEditor({ open, template, workTypes, onClose, onSaved }: {
  open: boolean;
  template: OfferTemplate | null;
  workTypes: WorkType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<OfferTemplateInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useLayoutEffect(() => {
    resetDirty();
    if (!open) return;
    setErr(null); setBusy(false);
    if (template) {
      setData({
        code: template.code,
        name: template.name,
        workType: template.workType,
        description: template.description ?? '',
        sampleSubject: template.sampleSubject ?? '',
        sampleBody: template.sampleBody ?? '',
        sampleLegalBasis: template.sampleLegalBasis ?? '',
        sampleTerms: template.sampleTerms ?? '',
        sampleExtraCosts: template.sampleExtraCosts ?? '',
        styleNotes: template.styleNotes ?? '',
        active: template.active,
      });
    } else {
      setData({ ...EMPTY, workType: workTypes[0]?.code ?? 'ANALIZA_RYZYKA' });
    }
  }, [open, template, workTypes]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: data, onClose, open, busy,
  });

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.name.trim()) { setErr('Nazwa wymagana'); return; }
    setBusy(true); setErr(null);
    const op = template
      ? offerTemplates.adminUpdate(template.id, data)
      : offerTemplates.adminCreate(data);
    op.then(() => onSaved())
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-[860px] max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {template ? `Edycja: ${template.name}` : 'Nowy wzorzec oferty'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="field col-span-2">
            <span className="field-label">Nazwa wzorca *</span>
            <input value={data.name} onChange={e => setData({...data, name: e.target.value})}
              className="input" placeholder="np. Analiza Ryzyka — gmina, jedno ujęcie"/>
          </label>
          <label className="field">
            <span className="field-label">Typ pracy *</span>
            <SearchableSelect value={data.workType} onChange={v => setData({...data, workType: v})}>
              {workTypes.map(wt => <option key={wt.code} value={wt.code}>{wt.label}</option>)}
            </SearchableSelect>
          </label>
          <label className="field flex-row items-center gap-2 mt-6">
            <input type="checkbox" checked={!!data.active} onChange={e => setData({...data, active: e.target.checked})}/>
            <span>Aktywny (używany przez AI)</span>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Opis (do czego wzorzec)</span>
            <input value={data.description ?? ''} onChange={e => setData({...data, description: e.target.value})} className="input"/>
          </label>
          <div className="field col-span-2">
            <span className="field-label">Notatka stylu (instrukcja dla AI: co podkreślić, jakich fraz używać)</span>
            <textarea value={data.styleNotes ?? ''} onChange={e => setData({...data, styleNotes: e.target.value})}
              className="textarea" rows={3} placeholder="np. Zwięzły styl. Zawsze używaj 'Ryczałtowa cena za jeden operat'…"/>
          </div>

          <div className="col-span-2 border-t border-ink-100 pt-3 mt-2">
            <div className="text-[12px] font-semibold text-ink-700 mb-2">Przykładowa treść (few-shot dla AI)</div>
          </div>

          <div className="col-span-2">
            <div className="field-label mb-1">Sekcja "Na…" (subject)</div>
            <TiptapEditor value={data.sampleSubject ?? ''} onChange={s => setData({...data, sampleSubject: s})} minHeight="100px"/>
          </div>
          <div className="col-span-2">
            <div className="field-label mb-1">Treść uzupełniająca (opcjonalnie)</div>
            <TiptapEditor value={data.sampleBody ?? ''} onChange={s => setData({...data, sampleBody: s})} minHeight="100px"/>
          </div>
          <div className="col-span-2">
            <div className="field-label mb-1">Podstawa prawna</div>
            <TiptapEditor value={data.sampleLegalBasis ?? ''} onChange={s => setData({...data, sampleLegalBasis: s})} minHeight="80px"/>
          </div>
          <div className="col-span-2">
            <div className="field-label mb-1">Termin / forma opracowania</div>
            <TiptapEditor value={data.sampleTerms ?? ''} onChange={s => setData({...data, sampleTerms: s})} minHeight="100px"/>
          </div>
          <div className="col-span-2">
            <div className="field-label mb-1">Dodatkowe koszty po stronie Zamawiającego</div>
            <TiptapEditor value={data.sampleExtraCosts ?? ''} onChange={s => setData({...data, sampleExtraCosts: s})} minHeight="80px"/>
          </div>

          {err && <div className="col-span-2 text-red-500 text-[12px]">Błąd: {err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : (template ? 'Zapisz zmiany' : 'Utwórz wzorzec')}
          </button>
        </div>
      </form>
    </div>
  );
}
